/**
 * geometryOps — AK-371 булевы операции и фигуры-примитивы для редактора контура.
 *
 * Чистый слой (без terra-draw / MapLibre / DOM) поверх turf: собирает поле
 * сложной формы (прямоугольник / круг / «Соединить» / «Вырезать» / «Дырка»)
 * и валидирует результат перед сохранением. Модель геометрии — MultiPolygon
 * с внутренними кольцами (ADR 2026-07-07): одно «поле» = одна MultiPolygon-
 * геометрия, части суммируются, дырки вычитаются (см. ADR § Decision).
 *
 * Все операции нормализуют результат к MultiPolygon-координатам
 * (`number[][][][]` = parts[part][ring][pt][lng|lat]; ring 0 — внешнее кольцо,
 * ring 1+ — дырки). Одиночный Polygon оборачивается в MultiPolygon из одной
 * части — редактор дальше работает единообразно, backend получает то, что
 * `ST_Multi(ST_GeomFromGeoJSON())` и так канонизирует.
 *
 * turf union/difference могут вернуть Polygon, MultiPolygon или null (пустой
 * результат вычитания) — здесь это приводится к нашей модели + понятным
 * ошибкам для UI.
 */
import union from '@turf/union';
import difference from '@turf/difference';
import kinks from '@turf/kinks';
import booleanIntersects from '@turf/boolean-intersects';
import { featureCollection, multiPolygon, polygon } from '@turf/helpers';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';

/** [lng, lat] — порядок MapLibre / GeoJSON. */
export type LngLat = [number, number];

/** Кольцо полигона (замкнутое: первая точка == последняя). */
export type Ring = LngLat[];

/** Один полигон: кольцо 0 — внешнее, кольца 1+ — дырки. */
export type PolygonRings = Ring[];

/** MultiPolygon-координаты: массив частей, каждая — набор колец. */
export type MultiPolygonCoords = PolygonRings[];

/** Результат булевой операции: успех с геометрией или ошибка с текстом для UI. */
export type BooleanResult =
  | { ok: true; coords: MultiPolygonCoords }
  | { ok: false; error: string };

// ── нормализация ────────────────────────────────────────────────────────────

/** Замыкает кольцо: добавляет первую точку в конец, если её там нет. */
export function ensureClosed(ring: LngLat[]): LngLat[] {
  if (ring.length === 0) return ring;
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (a[0] === b[0] && a[1] === b[1]) return ring;
  return [...ring, a];
}

/**
 * Приводит GeoJSON Polygon | MultiPolygon (координаты) к нашей
 * MultiPolygonCoords. Одиночный Polygon → MultiPolygon из одной части.
 */
export function toMultiPolygonCoords(
  geometry: Polygon | MultiPolygon,
): MultiPolygonCoords {
  if (geometry.type === 'Polygon') {
    return [ringsFromPolygon(geometry.coordinates)];
  }
  return geometry.coordinates.map((part) => ringsFromPolygon(part));
}

function ringsFromPolygon(rings: Position[][]): PolygonRings {
  return rings.map((ring) => ensureClosed(ring.map((c) => [c[0], c[1]] as LngLat)));
}

/**
 * MultiPolygonCoords → GeoJSON-геометрия. Если одна часть без дырок — отдаём
 * Polygon (компактнее и совместимо со старым single-ring путём), иначе
 * MultiPolygon. Backend принимает оба (ST_Multi канонизирует).
 */
export function coordsToGeometry(coords: MultiPolygonCoords): Polygon | MultiPolygon {
  const parts = coords.filter((p) => p.length && p[0] && p[0].length >= 4);
  if (parts.length === 1) {
    return { type: 'Polygon', coordinates: parts[0].map((r) => r.map((c) => c.slice() as LngLat)) };
  }
  return {
    type: 'MultiPolygon',
    coordinates: parts.map((part) => part.map((r) => r.map((c) => c.slice() as LngLat))),
  };
}

/** Внешнее кольцо каждой части — для рендера в terra-draw как отдельные фичи. */
export function outerRings(coords: MultiPolygonCoords): Ring[] {
  return coords.map((part) => part[0]).filter((r): r is Ring => !!r && r.length >= 4);
}

// ── фигуры-примитивы ──────────────────────────────────────────────────────────

/**
 * Прямоугольник по двум противоположным углам (drag угол-в-угол). Возвращает
 * замкнутое кольцо CCW-агностично (turf/PostGIS сами нормализуют ориентацию).
 */
export function rectangleRing(a: LngLat, b: LngLat): Ring {
  const [minLng, maxLng] = a[0] < b[0] ? [a[0], b[0]] : [b[0], a[0]];
  const [minLat, maxLat] = a[1] < b[1] ? [a[1], b[1]] : [b[1], a[1]];
  return [
    [minLng, minLat],
    [maxLng, minLat],
    [maxLng, maxLat],
    [minLng, maxLat],
    [minLng, minLat],
  ];
}

// ── булевы операции ───────────────────────────────────────────────────────────

/** Feature<Polygon|MultiPolygon> из наших координат (для turf). */
function toFeature(coords: MultiPolygonCoords): Feature<Polygon | MultiPolygon> {
  const geom = coordsToGeometry(coords);
  return geom.type === 'Polygon' ? polygon(geom.coordinates) : multiPolygon(geom.coordinates);
}

/** Одиночная фигура (внешнее кольцо) как Feature<Polygon> для turf. */
function ringFeature(ring: Ring): Feature<Polygon> {
  return polygon([ensureClosed(ring)]);
}

/**
 * «Соединить» — union текущего поля с нарисованной фигурой. Результат может
 * быть Polygon (смежные/пересекающиеся) или MultiPolygon (несмежные части).
 */
export function unionShape(current: MultiPolygonCoords, shape: Ring): BooleanResult {
  const shapeRing = ensureClosed(shape);
  if (shapeRing.length < 4) return { ok: false, error: 'Фигура слишком мала.' };
  if (!current.length) {
    // Поля ещё нет — фигура становится первой частью.
    return { ok: true, coords: [[shapeRing]] };
  }
  const merged = union(featureCollection([toFeature(current), ringFeature(shapeRing)]));
  if (!merged) return { ok: false, error: 'Не удалось соединить фигуры.' };
  return { ok: true, coords: toMultiPolygonCoords(merged.geometry) };
}

/**
 * «Вырезать» — difference: из поля вычитается нарисованная фигура. Пустой
 * результат (фигура покрыла всё поле) блокируется с внятным сообщением
 * (ADR R2). Разбиение поля на части — валидный MultiPolygon (это фича).
 */
export function subtractShape(current: MultiPolygonCoords, shape: Ring): BooleanResult {
  const shapeRing = ensureClosed(shape);
  if (shapeRing.length < 4) return { ok: false, error: 'Фигура слишком мала.' };
  if (!current.length) return { ok: false, error: 'Нет контура для вырезания.' };
  const diff = difference(featureCollection([toFeature(current), ringFeature(shapeRing)]));
  if (!diff) {
    return { ok: false, error: 'Вырезано всё поле — контур пуст. Отмените и вырежьте меньше.' };
  }
  return { ok: true, coords: toMultiPolygonCoords(diff.geometry) };
}

/**
 * «Дырка» — внутреннее исключение (донат). Частный случай «Вырезать»: фигура
 * целиком внутри поля даёт Polygon с внутренним кольцом. Реализация та же, что
 * subtractShape (turf difference сам добавляет фигуру как interior ring, когда
 * она внутри). Отдельная кнопка нужна для понятного UI (founder), логика общая.
 */
export function punchHole(current: MultiPolygonCoords, shape: Ring): BooleanResult {
  return subtractShape(current, shape);
}

// ── v2 «фигуры-первыми»: набор независимых фигур + булевы по клику ────────────
//
// Модель v2 (founder-UAT #443 → спека v2): редактор держит НАБОР фигур
// (каждая — PolygonRings, т.е. может иметь дырки), которые сосуществуют на
// карте. Булевы применяются к набору целиком:
//   - «Объединить» → union всех фигур в одно поле (MultiPolygon).
//   - «Вырезать» + клик по фигуре A → A вычитается из union остальных;
//     A исчезает, остальные получают вырез (внутренняя дырка при 100%-охвате,
//     либо ПУСТО когда A покрывает всё → обе удаляются, ADR R2).
// Пересечение фигур включает кнопки булевых (booleanIntersects, попарно).

/** Одна фигура-черновик редактора = набор колец (внешнее + дырки). */
export type ShapeParts = PolygonRings;

/** Итог «Вырезать по клику»: новый набор фигур + удалённые ли обе (пусто). */
export type CutResult =
  | { ok: true; shapes: ShapeParts[]; emptied: boolean }
  | { ok: false; error: string };

function shapeFeature(shape: ShapeParts): Feature<Polygon> | null {
  const rings = shape.filter((r) => r && r.length >= 4).map((r) => ensureClosed(r));
  if (!rings.length) return null;
  return polygon(rings);
}

/**
 * true, если хотя бы две фигуры набора пересекаются (по площади соприкасаются).
 * Включает доступность кнопок «Вырезать»/«Объединить» в панели. Касание только
 * границей turf booleanIntersects тоже считает пересечением — для UX этого
 * достаточно (пользователь видит наложение и хочет булеву).
 */
export function shapesIntersect(shapes: ShapeParts[]): boolean {
  const feats = shapes.map(shapeFeature).filter((f): f is Feature<Polygon> => !!f);
  for (let i = 0; i < feats.length; i++) {
    for (let j = i + 1; j < feats.length; j++) {
      try {
        if (booleanIntersects(feats[i], feats[j])) return true;
      } catch {
        // Вырожденная фигура — считаем не пересекающейся, идём дальше.
      }
    }
  }
  return false;
}

/**
 * «Объединить» — union ВСЕХ фигур набора в одно поле. Несмежные части остаются
 * MultiPolygon'ом (это валидно — одно поле из кусков). Пустой/одиночный набор
 * возвращается как есть.
 */
export function unionAllShapes(shapes: ShapeParts[]): BooleanResult {
  const feats = shapes.map(shapeFeature).filter((f): f is Feature<Polygon> => !!f);
  if (!feats.length) return { ok: false, error: 'Нет фигур для объединения.' };
  if (feats.length === 1) {
    return { ok: true, coords: toMultiPolygonCoords(feats[0].geometry) };
  }
  let acc: Feature<Polygon | MultiPolygon> = feats[0];
  for (let i = 1; i < feats.length; i++) {
    const merged = union(featureCollection([acc, feats[i]]));
    if (!merged) return { ok: false, error: 'Не удалось объединить фигуры.' };
    acc = merged;
  }
  return { ok: true, coords: toMultiPolygonCoords(acc.geometry) };
}

/**
 * «Вырезать» по клику: из union ОСТАЛЬНЫХ фигур вычитается фигура `targetIndex`.
 * Клик по фигуре A = «вырежи A из того, что под ней» (founder-модель v2).
 *
 * Кейсы:
 *   - A частично перекрывает B → B получает вырез (L-форма / раскол на части);
 *     A исчезает. Результат — набор из одной фигуры (вырезанный остаток).
 *   - A целиком внутри B → у B появляется внутреннее кольцо (дырка).
 *   - A целиком покрывает всё остальное (или остальных нет) → результат ПУСТ →
 *     обе/все фигуры удаляются (emptied=true; UI подтверждает/undo, не молча).
 *
 * Возвращает новый набор фигур (каждая часть difference — отдельная фигура,
 * чтобы дальше их можно было независимо править/резать снова).
 */
export function cutShapeFromRest(shapes: ShapeParts[], targetIndex: number): CutResult {
  const target = shapes[targetIndex];
  if (!target) return { ok: false, error: 'Фигура для выреза не найдена.' };
  const rest = shapes.filter((_, i) => i !== targetIndex);
  const restFeats = rest.map(shapeFeature).filter((f): f is Feature<Polygon> => !!f);
  const targetFeat = shapeFeature(target);
  if (!targetFeat) return { ok: false, error: 'Фигура для выреза вырождена.' };
  if (!restFeats.length) {
    // Резать не из чего — клик по единственной фигуре стирает её (пустой результат).
    return { ok: true, shapes: [], emptied: true };
  }
  // union остальных → одна геометрия, из которой вычитаем цель.
  let base: Feature<Polygon | MultiPolygon> = restFeats[0];
  for (let i = 1; i < restFeats.length; i++) {
    const merged = union(featureCollection([base, restFeats[i]]));
    if (!merged) return { ok: false, error: 'Не удалось подготовить фигуры к вырезу.' };
    base = merged;
  }
  const diff = difference(featureCollection([base, targetFeat]));
  if (!diff) {
    // Цель покрыла всё остальное без остатка → всё поле удаляется.
    return { ok: true, shapes: [], emptied: true };
  }
  // Разбиваем результат на отдельные фигуры (по частям MultiPolygon), чтобы
  // каждая оставалась независимо редактируемой/режущейся.
  const coords = toMultiPolygonCoords(diff.geometry);
  const newShapes = coords.filter((part) => part[0] && part[0].length >= 4);
  return { ok: true, shapes: newShapes, emptied: newShapes.length === 0 };
}

// ── валидация ─────────────────────────────────────────────────────────────────

/**
 * Проверка самопересечений (kinks) перед «Готово». turf/kinks возвращает точки
 * пересечения сегментов; непусто → геометрия невалидна для сохранения.
 * Возвращает null если валидно, либо текст ошибки для UI.
 */
export function validateNoKinks(coords: MultiPolygonCoords): string | null {
  if (!coords.length) return 'Контур пуст.';
  // turf/kinks не принимает MultiPolygon — проверяем каждую часть отдельно
  // (Polygon с её кольцами: внешнее + дырки). Самопересечение в любой части →
  // невалидно.
  for (const part of coords) {
    const rings = part.filter((r) => r && r.length >= 4).map((r) => ensureClosed(r));
    if (!rings.length) continue;
    const k = kinks(polygon(rings));
    if (k.features.length > 0) {
      return 'Контур пересекает сам себя — поправьте вершины перед сохранением.';
    }
  }
  return null;
}

/** true, если геометрия непустая и содержит хотя бы одну валидную часть. */
export function isNonEmpty(coords: MultiPolygonCoords): boolean {
  return outerRings(coords).length > 0;
}
